import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Users, Play, X } from "lucide-react";

const OPS = [
  { v: "eq", l: "equals" },
  { v: "ne", l: "not equals" },
  { v: "contains", l: "contains" },
  { v: "not_contains", l: "not contains" },
  { v: "in", l: "in (list)" },
  { v: "not_in", l: "not in" },
  { v: "gt", l: ">" },
  { v: "lt", l: "<" },
  { v: "gte", l: "≥" },
  { v: "lte", l: "≤" },
  { v: "exists", l: "exists" },
];
const FIELDS = ["tags", "email", "phone", "name", "list_ids", "dnd", "opted_out", "custom_fields.city", "last_message_at", "created_at"];

export default function Segments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", description: "", rules: { op: "AND", conditions: [{ field: "tags", operator: "contains", value: "" }] } };
  const [form, setForm] = useState(blank);
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/segments").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const addCond = () => setForm(f => ({ ...f, rules: { ...f.rules, conditions: [...f.rules.conditions, { field: "tags", operator: "contains", value: "" }] } }));
  const rmCond = (i) => setForm(f => ({ ...f, rules: { ...f.rules, conditions: f.rules.conditions.filter((_, j) => j !== i) } }));
  const setCond = (i, patch) => setForm(f => ({ ...f, rules: { ...f.rules, conditions: f.rules.conditions.map((c, j) => j === i ? { ...c, ...patch } : c) } }));

  const runPreview = async () => {
    try {
      // Cast list-like values for "in" ops
      const rules = {
        ...form.rules,
        conditions: form.rules.conditions.map(c => (c.operator === "in" || c.operator === "not_in") && typeof c.value === "string"
          ? { ...c, value: c.value.split(",").map(s => s.trim()).filter(Boolean) }
          : c),
      };
      const { data } = await api.post("/segments/preview", { ...form, rules });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };
  const save = async () => {
    const rules = { ...form.rules, conditions: form.rules.conditions.map(c => (c.operator === "in" || c.operator === "not_in") && typeof c.value === "string" ? { ...c, value: c.value.split(",").map(s => s.trim()).filter(Boolean) } : c) };
    try {
      if (editing) await api.patch(`/segments/${editing.id}`, { ...form, rules });
      else await api.post("/segments", { ...form, rules });
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(blank); setPreview(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete segment?")) return; await api.delete(`/segments/${id}`); load(); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description || "", rules: s.rules });
    setPreview(null); setOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="segments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Audience</div>
          <h1 className="text-3xl font-black tracking-tighter">Segments</h1>
          <p className="text-xs text-muted-foreground mt-1">Dynamic contact groups that update in real time. Use them as campaign targets.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); setPreview(null); } }}>
          <DialogTrigger asChild><Button className="rounded-sm gap-2" data-testid="new-segment-button"><Plus className="h-4 w-4" /> New Segment</Button></DialogTrigger>
          <DialogContent className="rounded-sm max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Segment</DialogTitle><DialogDescription>Build a rule that selects contacts dynamically.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="segment-name" /></div>
                <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">Match</span>
                  <select value={form.rules.op} onChange={e => setForm({ ...form, rules: { ...form.rules, op: e.target.value } })} className="h-7 px-2 rounded-sm border border-border bg-background text-xs" data-testid="segment-op">
                    <option value="AND">ALL conditions (AND)</option>
                    <option value="OR">ANY condition (OR)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  {form.rules.conditions.map((c, i) => (
                    <div key={i} className="flex flex-wrap gap-2 items-center border border-border rounded-sm p-2 bg-muted/20">
                      <select value={c.field} onChange={e => setCond(i, { field: e.target.value })} className="h-8 px-2 rounded-sm border border-border bg-background text-xs" data-testid={`cond-field-${i}`}>
                        {FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <select value={c.operator} onChange={e => setCond(i, { operator: e.target.value })} className="h-8 px-2 rounded-sm border border-border bg-background text-xs" data-testid={`cond-op-${i}`}>
                        {OPS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                      {c.operator !== "exists" && <Input value={c.value} onChange={e => setCond(i, { value: e.target.value })} placeholder={c.operator === "in" || c.operator === "not_in" ? "comma-separated" : "value"} className="rounded-sm h-8 text-xs w-40" data-testid={`cond-value-${i}`} />}
                      {c.operator === "exists" && (
                        <select value={c.value ? "true" : "false"} onChange={e => setCond(i, { value: e.target.value === "true" })} className="h-8 px-2 rounded-sm border border-border bg-background text-xs">
                          <option value="true">true</option><option value="false">false</option>
                        </select>
                      )}
                      <Button variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0 ml-auto" onClick={() => rmCond(i)}><X className="h-3 w-3" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={addCond} data-testid="add-condition"><Plus className="h-3 w-3" /> Add condition</Button>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" className="rounded-sm gap-1" onClick={runPreview} data-testid="preview-segment"><Play className="h-3 w-3" /> Preview</Button>
                {preview && <div className="text-xs">Matches: <span className="font-bold">{preview.count}</span> contacts</div>}
              </div>
              {preview?.sample?.length > 0 && (
                <div className="max-h-48 overflow-auto border border-border rounded-sm">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40"><tr><th className="px-2 py-1 text-left">Name</th><th className="px-2 py-1 text-left">Phone</th><th className="px-2 py-1 text-left">Tags</th></tr></thead>
                    <tbody>{preview.sample.map((c, i) => (<tr key={i} className="border-t border-border"><td className="px-2 py-1">{c.name}</td><td className="px-2 py-1 font-mono">{c.phone}</td><td className="px-2 py-1">{(c.tags || []).join(", ")}</td></tr>))}</tbody>
                  </table>
                </div>
              )}
            </div>
            <DialogFooter><Button onClick={save} className="rounded-sm" data-testid="save-segment">{editing ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(s => (
          <Card key={s.id} className="rounded-sm shadow-none cursor-pointer hover:bg-accent/40" onClick={() => openEdit(s)} data-testid={`segment-${s.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /><span className="font-semibold">{s.name}</span></div>
                <Button variant="ghost" size="sm" className="rounded-sm text-red-600 h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); del(s.id); }}><Trash2 className="h-3 w-3" /></Button>
              </div>
              <div className="text-xs text-muted-foreground">{s.description || "—"}</div>
              <div className="flex flex-wrap gap-1">{(s.rules?.conditions || []).slice(0, 3).map((c, i) => (
                <Badge key={i} variant="outline" className="rounded-sm text-[10px] font-mono">{c.field} {c.operator} {typeof c.value === "object" ? JSON.stringify(c.value) : String(c.value ?? "")}</Badge>
              ))}</div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <Card className="rounded-sm shadow-none md:col-span-3"><CardContent className="text-center text-muted-foreground py-10">No segments yet.</CardContent></Card>}
      </div>
    </div>
  );
}
